import * as React from 'react';
import classnames from 'classnames';
import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { AttributeValue, CartLine, CommerceList, Customer, ProductList, ProductPrice, ProductSearchResult, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    IProductInventoryInformation,
    OrderTemplate, getFallbackImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import { ICartlinesViewProps } from '@msdyn365-commerce-modules/cart';
import { ProductListRow } from './product-list-row';
import { useEffect, useMemo, useState } from 'react';
import PQueue from 'p-queue';
import { ValidateProductAccessResult } from '../../shared-utils/validate-product-access';
import { IProductAgreement, IProductError, ProductSearchResultExt, SimpleProductExt } from '../../shared-utils/types';
import { CountingListLine } from '../../actions/entities/counting-list';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import { ICartViewProps } from '../../modules/dlvry-cart/dlvry-cart';
import { IBrandSchemeEntity } from '../../actions/entities/brand-scheme-entity';
import ProductListRowShimmer from './product-list-row-shimmer';
import { IProductRelationTypeForProduct } from '../../actions/get-relation-types-by-ids';

// Modules where the ProductListComponent is used:
// - Cart
// - Search Result Container
// - Wishlist
// - Order Template

/** Types for getting extended properties only from ProductSearchResultExt and SimpleProductExt */
type ExtendedProperties = & Omit<ProductSearchResultExt | SimpleProductExt, keyof ProductSearchResult | keyof SimpleProduct>;
export type ProductListProduct = ExtendedProperties & Pick<
    ProductSearchResult | SimpleProduct,
    'RecordId' | 'ItemId' | 'Name' | 'PrimaryImageUrl' | 'DefaultUnitOfMeasure' | 'Price'
> & {
    InventoryLabel?: string;
    CurrentQuantity?: number;
    CartLine?: CartLine;
    Availability?: IProductInventoryInformation;
    PriceFailed?: boolean;
    DisplayOrder?: number;
    RequestedDeliveryDate?: Date;
    BasePrice?: number;
    LineCreatedDateTime?: Date;
};

export interface IProductListResources {
    buyNowButtonText: string;
    removeButtonText?: string;
    priceErrorText?: string;
    inCartTooltipText?: string;
    deliveryDateLabel?: string;
    transitTooltip?: string;
    agreementProductTooltip?: string;
    addToWishlistText?: string;
    removeFromWishlistText?: string;
    pricePerText?: string;
    priceNotLoggedInText?: string;
    alternativeProductsButtonText?: string;
    alternativeProductsChangeProductButtonText?: string;
    alternativeProductsLoadingText?: string;
    alternativeProductsNoResultsText?: string;
    alternativeProductsErrorText?: string;
}

export interface IActionsAvailable {
    addToCartAction?: boolean;
    addToCartOrderTemplate?: boolean;
    addToOrderTemplate?: boolean;
    addToWishlistAction?: boolean;
    removeAction?: boolean;
    productComparisonButton?: boolean;
}

interface IProductListItems {
    type?: 'default' | 'cart';
    cart?: ICartState;
    products: ProductListProduct[];
    simpleProducts: SimpleProduct[];
    unitConversions?: UnitOfMeasureConversion[];
    orderTemplates?: OrderTemplate[];
    productErrors?: IProductError[];
    context: ICoreContext;
    resources: IProductListResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    placeholderLines?: number;
    allowMultiSelect?: boolean;
    allowZeroQuantity?: boolean;
    orderTemplate?: OrderTemplate;
    customerInformation?: Customer;
    customerAgreements?: IProductAgreement[];
    brandSchemes?: IBrandSchemeEntity[];
    productComparisonButton?: {} | null | undefined;
    isLoading?: boolean;
    disableBuyButtons?: boolean;
    disableReorder?: boolean;
    disableQuantityInput?: boolean;
    draggable?: boolean;
    historicalPrice?: boolean;
    min?: number;
    showAvailability?: boolean;
    showPrices?: boolean;
    showTotalPrices?: boolean;
    showCartCount?: boolean;
    showGroups?: boolean;
    readOnly?: boolean;
    buyAlwaysAdd?: boolean;
    resetSelectedQuantities?: Set<number>;
    smallerImage?: boolean;
    smallestImage?: boolean;
    onResetSelectedQuantities?: () => void;
    validatedProductAccess?: ValidateProductAccessResult[];
    actionsAvailable?: IActionsAvailable;
    cartViewProps?: ICartViewProps | undefined;
    selectOnQuantityChange?: boolean;
    breakoffProducts?: { [key: number]: boolean };
    selectedProducts?: IProductSelection;
    wishlists?: CommerceList[];
    relationTypesByIds?: IProductRelationTypeForProduct[];
    onProductRemove?: (product: ProductListProduct) => Promise<void>;
    onUpdateOrderTemplates?: (product?: ProductListProduct, orderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    onUpdateQuantity?: (product: ProductListProduct, quantity: number) => void;
    onUpdateSelection?: (selection: IProductSelection) => void;
    onBreakoffChange?: (product: ProductListProduct, enabled: boolean) => void;
    onDrop?: (productRecId: number | undefined, targetRecId: number | undefined, direction: 'over' | 'under') => void;
    onBuyValidate?: (product: ProductListProduct, quantity: number) => Promise<BuyValidationResult>;
    retrieveOrderTemplates?(): Promise<OrderTemplate[]>;
    onChangeProduct?: (product: ProductListProduct, replacement: ProductListProduct) => void;
}

export interface IProductSelection {
    [key: string]: boolean;
}

export interface BuyValidationResult {
    isValid: boolean;
    message?: string;
}

export const ProductListComponent: React.FC<IProductListItems> = ({
    type = 'default',
    cart,
    products,
    simpleProducts,
    unitConversions,
    orderTemplates,
    productErrors,
    orderTemplate,
    customerInformation,
    customerAgreements,
    brandSchemes,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    placeholderLines,
    allowMultiSelect,
    allowZeroQuantity,
    productComparisonButton,
    isLoading,
    readOnly,
    disableBuyButtons,
    disableReorder,
    disableQuantityInput,
    draggable,
    historicalPrice,
    min,
    showAvailability,
    showPrices,
    showTotalPrices,
    showCartCount,
    showGroups,
    buyAlwaysAdd,
    resetSelectedQuantities,
    smallerImage,
    smallestImage,
    relationTypesByIds,
    onResetSelectedQuantities,
    validatedProductAccess,
    actionsAvailable,
    cartViewProps,
    selectOnQuantityChange,
    breakoffProducts,
    selectedProducts: selectedProductsProp,
    wishlists,
    onProductRemove,
    onUpdateOrderTemplates,
    onUpdateQuantity,
    onUpdateSelection,
    onBreakoffChange,
    onDrop,
    onBuyValidate,
    retrieveOrderTemplates,
    onChangeProduct
}) => {
    const [selectedProducts, setSelectedProducts] = useState<IProductSelection>(selectedProductsProp || {});
    const [dragOverProduct, setDragOverProduct] = useState<number | undefined>(undefined);
    const [dragOverUnder, setDragOverUnder] = useState<'over' | 'under' | undefined>(undefined);
    const cartQueue = useMemo(() => new PQueue({ concurrency: 1 }), []);

    useEffect(() => {
        setSelectedProducts(selectedProductsProp || {});
    }, [selectedProductsProp]);

    useEffect(() => {
        onUpdateSelection?.(selectedProducts);
    }, [selectedProducts]);

    React.useEffect(() => {
        document.querySelectorAll('.msc-add-to-order-template-icon').forEach(element => {
            element.setAttribute('title', element.getAttribute('aria-label') || '');
        });

        // Remove selection from products that are no longer available (for example on another page)
        Object.keys(selectedProducts).forEach(key => {
            const id = Number(key);

            if (isNaN(id)) {
                return;
            }

            if (products.every(x => x.RecordId !== id)) {
                delete selectedProducts[key];
            }
        });
    }, [products]);

    // Setup navigation with Enter and Shift+Enter for quantity inputs
    React.useEffect(() => {
        const inputs = document.querySelectorAll('.dlvry-quantity__quantity-input');
        const keyDownHandler = (e: Event) => {
            const event = e as KeyboardEvent;
            const index = Number((event.currentTarget as HTMLElement).getAttribute('data-index'));

            if (event.key === 'Enter') {
                if (event.shiftKey) {
                    if (index === 0) {
                        return;
                    }
                    (inputs.item(index - 1) as HTMLInputElement).select();
                } else {
                    if (index === inputs.length - 1) {
                        return;
                    }
                    (inputs.item(index + 1) as HTMLInputElement).select();
                }
            }
        };

        inputs.forEach((element, index) => {
            element.setAttribute('data-index', index.toString());
            element.addEventListener('keydown', keyDownHandler);
        });

        return () => {
            inputs.forEach(element => {
                element.removeEventListener('keydown', keyDownHandler);
            });
        };
    }, [products]);

    React.useEffect(() => {
        if (resetSelectedQuantities) {
            resetSelectedQuantities.forEach(id => {
                if (products.find(x => x.RecordId === id)) {
                    products.find(x => x.RecordId === id)!.CurrentQuantity = 0;
                }
            });

            onResetSelectedQuantities?.();
        }
    }, [resetSelectedQuantities]);

    const changeSelection = (id: number | undefined) => {
        if (id) {
            setSelectedProducts((prevState) => {
                prevState[id] = !prevState[id];
                return { ...prevState };
            });
        }
    };
    const handleDrop = async (e: React.DragEvent<HTMLElement>, recordId: number | undefined, overUnder: "over" | "under" | undefined) => {
        const dragData = JSON.parse(e.dataTransfer.getData('product'));
        const dragProduct = products.find(product => product.RecordId === dragData.ProductRecordId);
        const targetProduct = products.find(product => product.RecordId === recordId);

        if (!dragProduct || !targetProduct || !overUnder) {
            return;
        }

        onDrop?.(dragProduct?.RecordId, targetProduct?.RecordId, overUnder);
    };

    const grouped = products.reduce((acc, product) => {
        const key = showGroups && product.RequestedDeliveryDate ? new Date(product.RequestedDeliveryDate).getTime().toString() : 'default';
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(product);
        return acc;
    }, {} as { [key: string]: ProductListProduct[] });
    const createdGroupFor: string[] = [];

    return (
        <div>
            <ul
                className={classnames('product-list-component list product-listing')}
                onDrop={async e => {
                    await handleDrop(e, dragOverProduct, 'under');
                }}
                onDragOver={e => {
                    e.stopPropagation();

                    if (!disableReorder && e.dataTransfer.types.includes('product')) {
                        e.preventDefault();

                        const firstItem = document.querySelector('.product-list-component > li:first-of-type');
                        const margin = firstItem ? parseFloat(window.getComputedStyle(firstItem).marginBottom) + 6 : 22;
                        const item = document.elementFromPoint(e.clientX, e.clientY - margin);

                        let listItem = item?.parentElement;
                        while (listItem) {
                            if (listItem.tagName === 'LI') {
                                break;
                            }
                            listItem = listItem.parentElement;
                        }

                        if (!listItem) {
                            return;
                        }

                        const productId = listItem?.getAttribute('data-product-id') ? parseFloat(listItem.getAttribute('data-product-id')!) : undefined;

                        setDragOverProduct(productId);
                        setDragOverUnder('under');
                    }
                }}
            >
                {Object.keys(grouped).map((key, index) => {
                    const items: React.ReactNode[] = [];

                    if (key !== 'default' && !createdGroupFor.includes(key)) {
                        items.push(
                            <li key={`${key}-group`} className='product-list-group-header'>
                                {resources.deliveryDateLabel}: {context.cultureFormatter.formatDate(new Date(parseFloat(key)), { day: 'numeric', month: 'long' })}
                            </li>
                        );
                        createdGroupFor.push(key);
                    }

                    if (placeholderLines && placeholderLines > 0) {
                        for (let i = 0; i < placeholderLines - 3; i++) {
                            items.push(
                                <ProductListRowShimmer
                                    className='product-search-table-row'
                                    key={`${key}-placeholder-${i}`}
                                />
                            );
                        }
                        return items;
                    }

                    for (const product of grouped[key]) {
                        items.push((
                            <ProductListRow
                                key={`${product.RecordId}-${product.DefaultUnitOfMeasure}-${product.CartLine?.LineId}`}
                                context={context}
                                typeName='product-search-table-row'
                                id={`product-search-table-row-${index}`}
                                className='product-search-table-row'
                                resources={resources}
                                cartQueue={cartQueue}
                                selectOnQuantityChange={selectOnQuantityChange}
                                isDragOver={!disableReorder && product.RecordId === dragOverProduct ? dragOverUnder : undefined}
                                isSelected={product.RecordId ? selectedProducts[product.RecordId] : false}
                                onSelectCartHandler={() => changeSelection(product.RecordId)}
                                data={{
                                    cart,
                                    product,
                                    simpleProduct: simpleProducts.find(simpleProduct => simpleProduct.RecordId === product.RecordId),
                                    orderTemplate,
                                    customerInformation,
                                    customerAgreements,
                                    unitConversions: unitConversions?.filter(x => x.ProductRecordId === product.RecordId),
                                    wishlists,
                                    orderTemplates,
                                    brandSchemes
                                }}
                                productComparisonButton={productComparisonButton}
                                actionsAvailable={actionsAvailable}
                                cartViewProps={cartViewProps}
                                moduleId={moduleId}
                                moduleType={moduleType}
                                error={productErrors?.find(x => x.RecordId === product.RecordId)}
                                allowMultiSelect={allowMultiSelect}
                                allowZeroQuantity={allowZeroQuantity}
                                min={min}
                                readOnly={readOnly}
                                disableBuyButton={disableBuyButtons}
                                disableReorder={disableReorder}
                                disableQuantityInput={disableQuantityInput}
                                draggable={draggable}
                                historicalPrice={historicalPrice}
                                imageSettings={imageSettings}
                                showAvailability={showAvailability}
                                showPrices={showPrices}
                                showTotalPrices={showTotalPrices}
                                showCartCount={showCartCount}
                                buyAlwaysAdd={buyAlwaysAdd}
                                resetSelectedQuantities={!!product.RecordId && resetSelectedQuantities?.has(product.RecordId)}
                                smallerImage={smallerImage}
                                smallestImage={smallestImage}
                                relationTypesByIds={relationTypesByIds}
                                initialBreakoffEnabled={Boolean(product.RecordId && breakoffProducts?.[product.RecordId])}
                                userHasAccess={validatedProductAccess?.find(x => x.RecordId === product.RecordId)?.HasAccess}
                                onUpdateOrderTemplates={onUpdateOrderTemplates}
                                retrieveOrderTemplates={retrieveOrderTemplates}
                                onProductRemove={onProductRemove}
                                onUpdateQuantity={quantity => {
                                    onUpdateQuantity?.(product, quantity);
                                }}
                                onBreakoffChange={onBreakoffChange}
                                onDrop={handleDrop}
                                onDragOver={(recordId, overUnder) => {
                                    setDragOverProduct(recordId);
                                    setDragOverUnder(overUnder);
                                }}
                                onBuyValidate={onBuyValidate}
                                onChangeProduct={onChangeProduct}
                            />
                        ));
                    }

                    return items;
                })}
            </ul>

            {isLoading && (
                <div className='product-search-loading-spinner-wrapper'>
                    <div className='product-search-loading-spinner'></div>
                </div>
            )}
        </div>
    );
};

const getInventoryLabel = (
    channelInventoryConfigurationId: number | undefined,
    attributeValues: AttributeValue[] | undefined,
    recordId: number,
    productInventoryStatus?: any
): string | undefined => {
    if (productInventoryStatus) {
        const shippingInventory = productInventoryStatus.find((shipping: any) => shipping.ProductAvailableQuantity.ProductId === recordId);
        if (shippingInventory && shippingInventory.StockLevelLabel) {
            return shippingInventory.StockLevelLabel;
        }
    }
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(attributeValues)) {
        return undefined;
    }
    const inventoryAttribute = attributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
};

export const productSearchResults2ProductList = (
    searchResults: ProductSearchResultExt[],
    channelInventoryConfigurationId: number | undefined,
    cart?: ICartState,
    productInventoryStatus?: IProductInventoryInformation[]
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    if (!searchResults) {
        return productList;
    }

    searchResults.map((searchResult, index) => {
        const cartLine = cart?.cart.CartLines?.find(x => x.ProductId === searchResult.RecordId);
        const productAvailability = productInventoryStatus?.find(x => x.ProductAvailableQuantity.ProductId === searchResult.RecordId);

        productList.push({
            ...searchResult,
            InventoryLabel: getInventoryLabel(
                channelInventoryConfigurationId,
                searchResult.AttributeValues,
                searchResult.RecordId,
                productInventoryStatus
            ),
            CurrentQuantity: cartLine?.Quantity,
            CartLine: cartLine,
            Availability: productAvailability
        });
    });

    return productList;
};

export const fullProducts2ProductList = (
    fullProducts: FullProduct[] | undefined,
    productAvailabilities: IProductInventoryInformation[] | undefined
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    fullProducts?.map((fullProduct, index) => {
        const productDetails = fullProduct.ProductDetails as SimpleProductExt;

        productList.push({
            ...productDetails,
            InventoryLabel: productAvailabilities?.find(
                avail => avail.ProductAvailableQuantity.ProductId === productDetails.RecordId
            )?.StockLevelLabel,
            Availability: productAvailabilities?.find(avail => avail.ProductAvailableQuantity.ProductId === productDetails.RecordId)
        });
    });

    return productList;
};

export const cartLines2ProductList = (
    cartLines: ICartlinesViewProps[],
    productAvailabilities: IProductInventoryInformation[] | undefined,
    context: ICoreContext,
    activePrices: ProductPrice[] | undefined
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    cartLines?.map((cartLine, index) => {
        const product = cartLine.data?.product as SimpleProductExt | undefined;
        /* const price = cartLine.data?.cartline.Price || 0; */
        const activePrice = activePrices?.find(x => x.ProductId === product?.RecordId);
        const price = !activePrice ? 0 : cartLine.data?.cartline?.NetAmountWithoutTax ?
            cartLine.data?.cartline.NetAmountWithoutTax / (cartLine.data?.cartline?.Quantity || 1) :
            cartLine.data?.cartline.Price || 0;
        const fallImageUrl = getFallbackImageUrl(product?.ItemId, context.request.apiSettings);
        const productPrice: ProductPrice | undefined = !activePrice ? undefined :{
            ...activePrice || {},
            ...cartLine.data?.cartline || {}
        };
        const availability = productAvailabilities?.find(
            avail => avail.ProductAvailableQuantity.ProductId === product?.RecordId
        );

        productList.push({
            ...product!,
            PrimaryImageUrl: product?.PrimaryImageUrl || fallImageUrl,
            Price: price,
            InventoryLabel: availability?.StockLevelLabel,
            CurrentQuantity: cartLine.data?.cartline.Quantity,
            CartLine: cartLine.data?.cartline,
            Availability: availability,
            Badge: getDiscountBadge(productPrice, product?.FallbackBadge),
            ProductPrice: productPrice
        });
    });

    return productList;
};

export const orderTemplateLines2ProductList = (
    lines: CountingListLine[],
    productAvailabilities: IProductInventoryInformation[] | undefined
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    lines?.map((line, index) => {
        const availability = productAvailabilities?.find(
            avail => avail.ProductAvailableQuantity.ProductId === line.fullProduct?.ProductDetails.RecordId
        );
        const displayOrder = line.productListLine.ExtensionProperties?.find(prop => prop.Key === 'DISPLAYORDER')?.Value?.DecimalValue;

        const product: ProductListProduct  = {
            ...line.fullProduct!.ProductDetails,
            DefaultUnitOfMeasure: line.productListLine.UnitOfMeasure,
            InventoryLabel: availability?.StockLevelLabel,
            Availability: availability,
            DisplayOrder: displayOrder,
            CurrentQuantity: line.productListLine.Quantity,
            LineCreatedDateTime: line.productListLine.CreatedDateTime || line.productListLine.LastUpdated,
            BasePrice: line.fullProduct?.ProductDetails.BasePrice,
            PriceFailed: (line as any).PriceFailed
        };

        if (typeof product.LineCreatedDateTime === 'string') {
            product.LineCreatedDateTime = new Date(product.LineCreatedDateTime);
        }

        productList.push(product);
    });

    return productList;
};
